import { trackEvent } from '../../utils/analytics-v2'
import { logBreadcrumb } from '../../utils/monitorClient'
import { INITIAL_SUPPORT_FEEDBACK_VALUES, SupportFeedbackFormValues } from './types'

const SUPPORT_FEEDBACK_STORAGE_PREFIX = 'veho_support_feedback_'

/** Retrieves persisted support feedback form values from localStorage for the given conversation ID. */
export function getSavedSupportFormValues(conversationId: string): SupportFeedbackFormValues | null {
  try {
    const saved = localStorage.getItem(`${SUPPORT_FEEDBACK_STORAGE_PREFIX}${conversationId}`)
    if (saved) return { ...INITIAL_SUPPORT_FEEDBACK_VALUES, ...JSON.parse(saved) }
  } catch (error) {
    logBreadcrumb('Failed to read saved support feedback form values', { conversationId, error })
  }
  return null
}

/** Persists current support feedback form values to localStorage for the given conversation ID. */
export function saveSupportFormValues(conversationId: string, values: SupportFeedbackFormValues): void {
  try {
    localStorage.setItem(`${SUPPORT_FEEDBACK_STORAGE_PREFIX}${conversationId}`, JSON.stringify(values))
  } catch (error) {
    logBreadcrumb('Failed to save support feedback form values', { conversationId, error })
  }
}

/** Removes persisted support feedback form values from localStorage for the given conversation ID. */
export function clearSavedSupportFormValues(conversationId: string): void {
  try {
    localStorage.removeItem(`${SUPPORT_FEEDBACK_STORAGE_PREFIX}${conversationId}`)
  } catch (error) {
    logBreadcrumb('Failed to clear saved support feedback form values', { conversationId, error })
  }
}

interface SupportFeedbackQuestion {
  questionId: string
  questionDisplayName: string
  answer: string
  questionType: 'rating' | 'multiSelect' | 'boolean' | 'text'
}

/**
 * Converts support feedback form values into a structured array of question/answer pairs for submission.
 * The 'overallSupportExperience' questionId and its 'rating' type are load-bearing — Toutatis's
 * createSupportConversationFeedback keys on them to record a support case for ratings <= 2.
 */
export function buildSupportFeedbackPayload(values: SupportFeedbackFormValues): SupportFeedbackQuestion[] {
  const questions: SupportFeedbackQuestion[] = [
    {
      questionId: 'overallSupportExperience',
      questionDisplayName: 'Overall Support Experience',
      answer: String(values.overallSupportExperience ?? ''),
      questionType: 'rating',
    },
    {
      questionId: 'supportIssues',
      questionDisplayName: 'Support Issues',
      answer: JSON.stringify(values.supportIssues),
      questionType: 'multiSelect',
    },
    {
      questionId: 'supportComments',
      questionDisplayName: 'Support Comments',
      answer: values.supportComments,
      questionType: 'text',
    },
    {
      questionId: 'consentToFollowUp',
      questionDisplayName: 'Consent to Follow Up',
      answer: String(values.consentToFollowUp),
      questionType: 'boolean',
    },
  ]

  return questions.filter(q => q.answer !== '' && q.answer !== '[]')
}

/** Fires a support_feedback_form_submitted analytics event with the consumer's responses. */
export function trackSupportFeedbackSubmission(values: SupportFeedbackFormValues, conversationId: string) {
  trackEvent('support_feedback_form_submitted', {
    conversationId,
    overallSupportExperience: values.overallSupportExperience,
    supportIssues: values.supportIssues,
    consentToFollowUp: values.consentToFollowUp,
  })
}
